/**
 * GTM tracking utilities for confirmation dialogs
 * Separates tracking logic from UI components for better testability and maintainability
 */

import { GTMTrackingAttributes, createGTMTracking, GtmEventKey } from './gtm-tracking';

export interface ConfirmationDialogTrackingConfig {
  /** GTM event key. */
  gtm?: GtmEventKey;
  /** GTM origin (data-origin) */
  origin?: string;
}

export type ButtonIntent =
  | 'primary'
  | 'secondary'
  | 'light'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'muted'
  | 'success-soft'
  | 'danger-soft';

export interface ButtonTrackingInfo {
  intent?: ButtonIntent;
  isLastButton: boolean;
}

/**
 * Confirm vs cancel for GTM `target`.
 * Visual `secondary` is noir (contained confirm), not “cancel”.
 * Cancel is the non-last outlined action; confirm is last, primary, or danger.
 */
const isConfirmButton = (info: ButtonTrackingInfo): boolean => {
  return (
    info.intent === 'danger' ||
    info.intent === 'primary' ||
    info.isLastButton
  );
};

/**
 * Creates GTM tracking attributes for a confirmation dialog action button
 */
export const getConfirmationDialogButtonTracking = (
  config: ConfirmationDialogTrackingConfig,
  buttonInfo: ButtonTrackingInfo
): Partial<GTMTrackingAttributes> => {
  if (!config.gtm) return {};
  
  const target = isConfirmButton(buttonInfo) ? 'confirmed' : 'cancelled';
  return createGTMTracking(config.gtm, {
    origin: config.origin,
    target,
  });
};

/**
 * Creates GTM tracking attributes for a confirmation dialog close button
 */
export const getConfirmationDialogCloseTracking = (
  config: ConfirmationDialogTrackingConfig
): Partial<GTMTrackingAttributes> => {
  if (!config.gtm) return {};
  return createGTMTracking(config.gtm, {
    origin: config.origin,
    target: 'closed',
  });
};
